// Customer Risk Register — lifecycle constants and status transition map.
//
// Shared code consumed by both client and server. Defines the canonical set of
// statuses, categories, severities, event types, and source types for risks, plus
// the allowed status-transition state machine and helpers used to validate
// transitions and build the 409 INVALID_TRANSITION error payload.
//
// Lifecycle state machine:
//
//   identified ──▶ recommended ──▶ accepted ──▶ in_progress ──▶ resolved
//        │              │              │
//        └──────────────┴──────────────┴──▶ deferred / risk_accepted
//                                                 (reopen → identified)
//
// Reopen: terminal (`resolved`), `deferred`, and `risk_accepted` all transition
// back to `identified`.

/** All valid risk lifecycle statuses. */
export const RISK_STATUSES = [
  "identified",
  "recommended",
  "accepted",
  "in_progress",
  "resolved",
  "deferred",
  "risk_accepted",
] as const;

/** Risk categories. */
export const RISK_CATEGORIES = [
  "security",
  "infrastructure",
  "compliance",
  "process",
  "training",
] as const;

/** Risk severities (highest to lowest). */
export const RISK_SEVERITIES = ["critical", "high", "medium", "low"] as const;

/** Risk event types recorded on the append-only history. */
export const RISK_EVENT_TYPES = [
  "status_change",
  "funded",
  "metric_update",
  "note",
  "reopened",
] as const;

/** Origin sources a risk can be created from. */
export const RISK_SOURCE_TYPES = [
  "assessment",
  "tech_stack_audit",
  "m365",
  "asset",
  "manual",
] as const;

export type RiskStatus = (typeof RISK_STATUSES)[number];
export type RiskCategory = (typeof RISK_CATEGORIES)[number];
export type RiskSeverity = (typeof RISK_SEVERITIES)[number];
export type RiskEventType = (typeof RISK_EVENT_TYPES)[number];
export type RiskSourceType = (typeof RISK_SOURCE_TYPES)[number];

/**
 * Allowed status transitions. The value is the set of statuses that `from` may
 * legally transition to. A status with an empty array is terminal (none today —
 * resolved/deferred/risk_accepted can all reopen to `identified`).
 */
export const ALLOWED_TRANSITIONS: Record<RiskStatus, readonly RiskStatus[]> = {
  // Forward path + early off-ramps to deferred / risk_accepted.
  identified: ["recommended", "deferred", "risk_accepted"],
  recommended: ["accepted", "deferred", "risk_accepted"],
  accepted: ["in_progress", "deferred", "risk_accepted"],
  in_progress: ["resolved"],
  // Reopen paths: terminal/parked states return to `identified`.
  resolved: ["identified"],
  deferred: ["identified"],
  risk_accepted: ["identified"],
} as const;

/**
 * Returns true when transitioning a risk from `from` to `to` is permitted by the
 * lifecycle state machine. Unknown statuses and no-op (`from === to`) transitions
 * are rejected.
 */
export function isAllowedTransition(from: RiskStatus, to: RiskStatus): boolean {
  const next = ALLOWED_TRANSITIONS[from];
  if (!next) {
    return false;
  }
  return next.includes(to);
}

/**
 * Returns the statuses a risk in `from` may legally transition to. Used to build
 * the 409 INVALID_TRANSITION error payload so callers can see valid next states.
 * Returns an empty array for unknown statuses.
 */
export function getAllowedNextStates(from: RiskStatus): RiskStatus[] {
  const next = ALLOWED_TRANSITIONS[from];
  return next ? [...next] : [];
}
